import NeptuneElement from "../../NeptuneElement.js";

class Dropdown extends NeptuneElement {

    templateMarkup = `
        <neptune-button type="tertiary" size="default" icon-end="fa-regular fa-caret-down" aria-haspopup="menu" aria-expanded="false">
        </neptune-button>
        <neptune-menu aria-label="dropdown options">
            <slot name="menu-item" slot="menu-item"></slot>
        </neptune-menu>
    `;

    componentCssUrl = '/src/components/form/dropdown/dropdown.css';

    #triggerButton = null;
    #listPanel = null;
    #handleTriggerClickBound = null;
    #handleMenuClickBound = null;

    constructor() {
        super();
    }

    async connectedCallback() {
        await super.connectedCallback();
        this.processInitialAttributesAfterRender(Dropdown.observedAttributes);
    }

    async disconnectedCallback() {
        this.removeEventListeners();
        await super.disconnectedCallback();
    }

    static get observedAttributes() {
        return ['label', 'open', 'disabled', 'type', 'size', 'multiselect'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) return;
        this.processAttribute(name, oldValue, newValue);
    }

    /**
     * Processes attribute changes and updates component state accordingly.
     * @param {string} name The attribute name.
     * @param {string} oldValue The previous attribute value.
     * @param {string} newValue The new attribute value.
     */
    processAttribute(name, oldValue, newValue) {
        if (oldValue === newValue) return;
        switch (name) {
            case 'label':
                this.#updateTriggerLabel();
                break;
            case 'open':
                this.#processOpenAttribute();
                break;
            case 'disabled':
                this.#processDisabledAttribute();
                break;
            case 'type':
                this.#processTypeAttribute();
                break;
            case 'size':
                this.#processSizeAttribute();
                break;
            case 'multiselect':
                this.#processMultiselectAttribute();
                break;
        }
    }

    /**
     * Attaches event listeners to the dropdown's trigger button and menu.
     */
    setEventListeners() {
        this.#triggerButton = this.shadowRoot?.querySelector('neptune-button');
        this.#listPanel = this.shadowRoot?.querySelector('neptune-menu');

        this.#handleTriggerClickBound = this.#handleTriggerClick.bind(this);
        this.#handleMenuClickBound = this.#handleMenuClick.bind(this);

        this.#triggerButton?.addEventListener('click', this.#handleTriggerClickBound);
        this.addEventListener('click', this.#handleMenuClickBound);
    }

    /**
     * Removes all event listeners and cleans up handler references.
     */
    removeEventListeners() {
        this.#triggerButton?.removeEventListener('click', this.#handleTriggerClickBound);
        this.removeEventListener('click', this.#handleMenuClickBound);
        this.#triggerButton = null;
        this.#listPanel = null;
        this.#handleTriggerClickBound = null;
        this.#handleMenuClickBound = null;
    }

    /**
     * Opens the dropdown menu panel.
     * Does nothing if the dropdown is disabled.
     */
    open() {
        if (this.hasAttribute('disabled')) return;
        this.setAttribute('open', '');
        this.#processOpenAttribute();
    }

    /**
     * Closes the dropdown menu panel.
     */
    close() {
        this.removeAttribute('open');
        this.#processOpenAttribute();
    }

    /**
     * Updates the trigger button text with the current label attribute.
     */
    #updateTriggerLabel() {
        const button = this.shadowRoot?.querySelector('neptune-button');
        if (button) {
            button.textContent = this.getAttribute('label') ?? '';
        }
    }

    /**
     * Handles the 'open' attribute change and updates the menu panel and trigger button.
     */
    #processOpenAttribute() {
        const isOpen = this.hasAttribute('open');
        if (!this.#listPanel) return;

        if (isOpen) {
            if (!this.#listPanel.hasAttribute('open')) {
                this.#listPanel.setAttribute('open', '');
            }
        } else {
            if (this.#listPanel.hasAttribute('open')) {
                this.#listPanel.removeAttribute('open');
            }
        }
        this.#triggerButton?.setAttribute('aria-expanded', String(isOpen));
    }

    /**
     * Handles the 'disabled' attribute change and updates the trigger button state.
     */
    #processDisabledAttribute() {
        const isDisabled = this.hasAttribute('disabled');
        if (isDisabled) {
            this.close();
            this.#triggerButton?.setAttribute('disabled', '');
        } else {
            this.#triggerButton?.removeAttribute('disabled');
        }
    }

    /**
     * Handles trigger button click events. Toggles the dropdown open/closed state.
     */
    #handleTriggerClick() {
        if (this.#listPanel?.hasAttribute('open')) {
            this.close();
        } else {
            this.open();
        }
    }

    /**
     * Handles menu click selection and keeps checkbox/filter rows open.
     * @param {MouseEvent} event
     */
    #handleMenuClick(event) {
        const menuItem = event.target?.closest?.('neptune-menu-item');
        if (!menuItem || !this.contains(menuItem) || menuItem.hasAttribute('disabled')) {
            return;
        }

        const isToggleControl = event.target?.closest?.('neptune-checkbox, input[type="checkbox"], neptune-switch, neptune-radio-button');
        if (isToggleControl) {
            return;
        }

        const label = this.#getMenuItemLabel(menuItem);
        this.close();
        this.dispatchEvent(new CustomEvent('neptune-dropdown-change', {
            bubbles: true,
            composed: true,
            detail: { label }
        }));
    }

    /**
     * Extracts a display label from a selected menu item.
     * @param {HTMLElement} menuItem
     * @returns {string}
     */
    #getMenuItemLabel(menuItem) {
        const explicitLabel = menuItem.getAttribute('label');
        if (explicitLabel) {
            return explicitLabel;
        }

        const activatable = menuItem.querySelector('neptune-button, button, a, [role="button"]');
        const text = (activatable?.textContent || menuItem.textContent || '').trim();
        return text;
    }

    /**
     * Updates the trigger button text with the selected item label.
     * @param {string} text The selected item label.
     */

    /**
     * Handles the 'type' attribute change and updates the trigger button type.
     */
    #processTypeAttribute() {
        const type = this.getAttribute('type');
        const button = this.shadowRoot?.querySelector('neptune-button');
        if (button) {
            if (type) {
                button.setAttribute('type', type);
            } else {
                button.removeAttribute('type');
            }
        }
    }

    /**
     * Handles the 'size' attribute change and updates the trigger button size.
     */
    #processSizeAttribute() {
        const size = this.getAttribute('size');
        const button = this.shadowRoot?.querySelector('neptune-button');
        if (button) {
            if (size) {
                button.setAttribute('size', size);
            } else {
                button.removeAttribute('size');
            }
        }
    }

    /**
     * Propagates the multiselect attribute to the inner neptune-menu.
     */
    #processMultiselectAttribute() {
        const menu = this.shadowRoot?.querySelector('neptune-menu');
        if (!menu) return;
        if (this.hasAttribute('multiselect')) {
            menu.setAttribute('multiselect', '');
        } else {
            menu.removeAttribute('multiselect');
        }
    }
}

customElements.define('neptune-dropdown', Dropdown);
export default Dropdown;

